import React, { useEffect, useState } from 'react';
import PageHeader from '../UI/PageHeader';
import { TicketList } from './TicketList';
import { TicketView } from './TicketView';
import { TicketForm } from './TicketForm';
import { TicketDashboard } from './TicketDashboard';
import { useTickets, useTicket } from '../../hooks/useTickets';
import { CreateTicketRequest, UpdateTicketRequest, Ticket } from '../../types/ticket';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

type ViewMode = 'list' | 'view' | 'create' | 'edit' | 'dashboard';

interface TicketFormWrapperProps {
  ticketId: number;
  onSave: (data: UpdateTicketRequest) => Promise<Ticket | null>;
  onCancel: () => void;
}

const TicketFormWrapper: React.FC<TicketFormWrapperProps> = ({ ticketId, onSave, onCancel }) => {
  const { ticket, loading } = useTicket(ticketId);
  const { t } = useTranslation();

  if (loading) {
    return <div>{t('tickets.loading')}</div>;
  }

  return <TicketForm ticket={ticket || undefined} onSave={onSave} onCancel={onCancel} />;
};

export const TicketsPage: React.FC = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const { createTicket, updateTicket } = useTickets();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleViewTicket = (id: number) => {
    setSelectedTicketId(id);
    setViewMode('view');
  };

  const handleEditTicket = (id: number) => {
    setSelectedTicketId(id);
    setViewMode('edit');
  };

  const handleCreateTicket = () => {
    setSelectedTicketId(null);
    setViewMode('create');
  };

  const handleSaveTicket = async (data: CreateTicketRequest | UpdateTicketRequest): Promise<Ticket | null> => {
    try {
      if (viewMode === 'create') {
        const result = await createTicket(data as CreateTicketRequest);
        if (result) {
          toast.success(t('tickets.toasts.createSuccess'));
          setRefreshTrigger((k) => k + 1);
        }
        handleBack();
        return result;
      } else if (viewMode === 'edit' && selectedTicketId) {
        const result = await updateTicket(selectedTicketId, data as UpdateTicketRequest);
        if (result) {
          toast.success(t('tickets.toasts.updateSuccess'));
          setRefreshTrigger((k) => k + 1);
        }
        handleBack();
        return result;
      }
      return null;
    } catch (error) {
      console.error('Erro ao salvar ticket:', error);
      return null;
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedTicketId(null);
    setTabValue(0);
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setViewMode('list');
    } else if (newValue === 1) {
      setViewMode('dashboard');
    }
    setSelectedTicketId(null);
  };

  const isModalOpen = viewMode === 'view' || viewMode === 'edit' || viewMode === 'create';

  useEffect(() => {
    if (!isModalOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleBack();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const tabs: { label: string; value: number }[] = [
    { label: t('tickets.tabs.list'), value: 0 },
    { label: t('tickets.tabs.dashboard'), value: 1 },
  ];

  return (
    <div>
      <PageHeader
        title={t('tickets.title')}
        subtitle={t('tickets.subtitle')}
      />

      <div className='border-b border-gray-200 dark:border-gray-700 mb-6'>
        <nav className='-mb-px flex gap-6'>
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                tabValue === tab.value
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {tabValue === 0 && (
        <TicketList
          onViewTicket={handleViewTicket}
          onEditTicket={handleEditTicket}
          onCreateTicket={handleCreateTicket}
          refreshTrigger={refreshTrigger}
        />
      )}

      {tabValue === 1 && (
        <TicketDashboard />
      )}

      {isModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6'>
          <div
            className='fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300'
            onClick={handleBack}
            aria-hidden='true'
          />
          <div className='relative bg-white dark:bg-gray-800 rounded-2xl shadow-glass-lg max-w-4xl w-full mx-auto max-h-[90vh] overflow-y-auto border border-white/20 dark:border-white/10'>
            {viewMode === 'view' && selectedTicketId && (
              <TicketView
                ticketId={selectedTicketId}
                onBack={handleBack}
                onEdit={() => setViewMode('edit')}
              />
            )}
            {viewMode === 'edit' && selectedTicketId && (
              <TicketFormWrapper
                ticketId={selectedTicketId}
                onSave={handleSaveTicket}
                onCancel={handleBack}
              />
            )}
            {viewMode === 'create' && (
              <TicketForm
                onSave={handleSaveTicket}
                onCancel={handleBack}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
